/**
 * Formatting Utilities
 * Functions for formatting dates, numbers, and other data
 */

/**
 * Format date to Spanish locale string
 * @param {Date|Timestamp} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatDate(date, options = {}) {
  if (!date) return "-";
  
  // Handle Firebase Timestamp
  const jsDate = date.toDate ? date.toDate() : new Date(date);
  
  const defaultOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  };
  
  return jsDate.toLocaleString("es-ES", defaultOptions);
}

/**
 * Format date to short format (DD/MM/YYYY)
 * @param {Date|Timestamp} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDateShort(date) {
  if (!date) return "-";
  
  const jsDate = date.toDate ? date.toDate() : new Date(date);
  
  return jsDate.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/**
 * Format date to time only (HH:MM)
 * @param {Date|Timestamp} date - Date to format
 * @returns {string} Formatted time string
 */
export function formatTime(date) {
  if (!date) return "-";
  
  const jsDate = date.toDate ? date.toDate() : new Date(date);
  
  return jsDate.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format file size to human-readable string
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size (e.g., "1.5 MB")
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  
  const units = ["B", "KB", "MB", "GB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
}

/**
 * Format phone number for display
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone
 */
export function formatPhone(phone) {
  if (!phone) return "";
  
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, "");
  
  // If starts with +34, format as Spanish phone
  if (cleaned.startsWith("+34")) {
    const number = cleaned.slice(3);
    if (number.length === 9) {
      return `+34 ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
    }
  }
  
  return cleaned;
}

/**
 * Truncate string to max length with ellipsis
 * @param {string} str - String to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated string
 */
export function truncate(str, maxLength = 50) {
  if (!str || str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "…";
}

/**
 * Format name to title case
 * @param {string} name - Name to format
 * @returns {string} Formatted name
 */
export function formatName(name) {
  if (!name) return "";
  
  return name
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Format DNI with proper spacing
 * @param {string} dni - DNI to format
 * @returns {string} Formatted DNI (12345678-A)
 */
export function formatDNI(dni) {
  if (!dni) return "";
  
  const cleaned = dni.trim().toUpperCase().replace(/\s/g, "");
  
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 8)}-${cleaned.slice(8)}`;
  }
  
  return cleaned;
}

/**
 * Get relative time string (e.g., "hace 2 horas")
 * @param {Date|Timestamp} date - Date to compare
 * @returns {string} Relative time string
 */
export function getRelativeTime(date) {
  if (!date) return "-";
  
  const jsDate = date.toDate ? date.toDate() : new Date(date);
  const now = new Date();
  const diffMs = now - jsDate;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return "hace un momento";
  if (diffMin < 60) return `hace ${diffMin} minuto${diffMin !== 1 ? "s" : ""}`;
  if (diffHour < 24) return `hace ${diffHour} hora${diffHour !== 1 ? "s" : ""}`;
  if (diffDay < 7) return `hace ${diffDay} día${diffDay !== 1 ? "s" : ""}`;
  
  return formatDateShort(jsDate);
}

/**
 * Pluralize word based on count
 * @param {number} count - Count
 * @param {string} singular - Singular form
 * @param {string} plural - Plural form (optional, adds 's' by default)
 * @returns {string} Pluralized string with count
 */
export function pluralize(count, singular, plural = null) {
  const word = count === 1 ? singular : (plural || singular + "s");
  return `${count} ${word}`;
}
